/**
 * BookingItem Component
 *
 * Displays a single booking line item with heading (hours) and body (project info).
 * Used within WeekDayCell to show individual bookings.
 *
 * @example
 * ```tsx
 * <BookingItem
 *   heading="8 timer"
 *   body="PL-0197-136 - Aker BP - IO Reports - Request #136 P&I Plan"
 *   type="project"
 * />
 * ```
 */

import classNames from "classnames";
import React from "react";
import styles from "./BookingItem.module.css";
import type { BookingItemProps } from "./types";

export const BookingItem: React.FC<BookingItemProps> = ({
  heading,
  body,
  type = "project",
  empty = false,
  className,
  "data-testid": testId,
}) => {
  const itemClasses = classNames(
    styles.item,
    type === "absence" && styles.absence,
    type === "softBooking" && styles.softBooking,
    (type === "empty" || empty) && styles.empty,
    className,
  );

  // Empty state - display "Ingen timer" message
  if (empty) {
    return (
      <div
        className={itemClasses}
        data-testid={testId}
        data-component-name="booking-item"
      >
        <span className={styles.emptyText}>Ingen timer</span>
      </div>
    );
  }

  return (
    <div
      className={itemClasses}
      data-testid={testId}
      data-component-name="booking-item"
    >
      <span
        className={styles.heading}
        data-testid={testId ? `${testId}-heading` : undefined}
      >
        {heading}
      </span>
      <span
        className={styles.body}
        data-testid={testId ? `${testId}-body` : undefined}
      >
        {body}
      </span>
    </div>
  );
};

BookingItem.displayName = "BookingItem";

export default BookingItem;
